'use client';

import ReactJsonView from '@microlink/react-json-view';
import { Input, Typography, Card } from 'antd';
import type {
  JsonViewContainerOptions,
  JsonViewEmptyState,
  JsonViewPlainTextOptions,
  JsonViewViewerOptions,
} from './types';
import { useJsonViewState } from './hooks/useJsonViewState';

const { Text } = Typography;

export interface JsonViewProps
  extends
    JsonViewViewerOptions,
    JsonViewContainerOptions,
    JsonViewPlainTextOptions,
    JsonViewEmptyState {
  value?: unknown;
  onChange?: (value: unknown) => void;
  editable?: boolean;
}

export function JsonView({
  value,
  onChange,
  editable = false,
  emptyText,
  rows = 18,
  placeholder,
  maxHeight = 520,
  minHeight,
  className,
  style,
  wrapInCard = true,
  appearance = 'light',
  name = false,
  theme,
  collapsed = false,
  collapseStringsAfterLength,
  displayArrayKey = true,
  displayDataTypes = false,
  displayObjectSize = true,
  enableClipboard = true,
  groupArraysAfterLength,
  iconStyle = 'triangle',
  indentWidth = 2,
  quotesOnKeys = false,
  sortKeys,
}: JsonViewProps) {
  const { resolved, src, commitInteraction } = useJsonViewState({
    value,
    onChange,
  });

  const resolvedTheme =
    theme ?? (appearance === 'technical' ? 'ocean' : 'rjv-default');

  const containerStyle =
    appearance === 'technical'
      ? {
          background: 'var(--evimo-code-bg)',
          border: '1px solid var(--evimo-code-border)',
          color: 'var(--evimo-code-fg)',
          borderRadius: 8,
          padding: 12,
          overflowY: 'auto' as const,
          overflowX: 'hidden' as const,
          maxHeight,
          minHeight,
          ...style,
        }
      : {
          background: 'var(--ant-colorFillAlter, #fafafa)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 8,
          padding: 12,
          overflowY: 'auto' as const,
          overflowX: 'hidden' as const,
          maxHeight,
          minHeight,
          ...style,
        };

  if (resolved.kind === 'empty') {
    return (
      <div className={className} style={containerStyle}>
        {emptyText ?? <Text type="secondary">—</Text>}
      </div>
    );
  }

  if (resolved.kind === 'plain-text') {
    if (editable) {
      return (
        <Input.TextArea
          className={className}
          value={resolved.text}
          rows={rows}
          placeholder={placeholder}
          onChange={(event) => onChange?.(event.target.value)}
          style={{
            fontFamily: 'monospace',
            fontSize: 13,
            ...style,
          }}
        />
      );
    }

    return (
      <pre
        className={className}
        style={{
          ...containerStyle,
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: 13,
          lineHeight: 1.5,
          fontFamily: 'monospace',
        }}
      >
        {resolved.text}
      </pre>
    );
  }

  if (!src) {
    return null;
  }

  const viewer = (
    <ReactJsonView
      src={src}
      name={name}
      theme={resolvedTheme}
      collapsed={collapsed}
      collapseStringsAfterLength={collapseStringsAfterLength}
      displayArrayKey={displayArrayKey}
      displayDataTypes={displayDataTypes}
      displayObjectSize={displayObjectSize}
      enableClipboard={enableClipboard}
      groupArraysAfterLength={groupArraysAfterLength}
      iconStyle={iconStyle}
      indentWidth={indentWidth}
      quotesOnKeys={quotesOnKeys}
      sortKeys={sortKeys}
      style={{ fontSize: 13, backgroundColor: 'transparent' }}
      onEdit={editable ? commitInteraction : false}
      onAdd={editable ? commitInteraction : false}
      onDelete={editable ? commitInteraction : false}
    />
  );

  if (!wrapInCard) {
    return (
      <div className={className} style={containerStyle}>
        {viewer}
      </div>
    );
  }

  return (
    <Card
      className={className}
      style={containerStyle}
      variant="borderless"
      styles={{ body: { padding: 0 } }}
    >
      {viewer}
    </Card>
  );
}
